import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getValidationPreview } from '../../apis/bounties/bounties';
import Table from '../../ui/Table';
import Legend from '../../ui/Legend';
import NotIssuedIcon from '../../assets/icons/NotIssuedIcon';
import IssuedIcon from '../../assets/icons/IssuedIcon';

import ButtonNew from '../../ui/ButtonNew';
import { toast } from 'sonner';
import Loader from '../../ui/Loader';
import Modal from '../../ui/Modal';
import TokenApprovalModal from './TokenApprovalModal';
import InsufficientBalanceModal from './InsufficientBalanceModal';

import { useLoadingState } from '../../hooks/useLoader';
import { FirstRank, SecondRank, ThirdRank } from '../../assets/icons/RankIcons';
import NoDataAvailable from '../../ui/NoDataAvailable';
import { toNormalRepresentation } from '../../helpers/convertIntoDecimals';

import { useSDK } from '@metamask/sdk-react';
import { requestINRFundTransfer, validating } from '../../apis/validator/validating';
import {
    fetchChainDetails,
    finaliseGrading,
    finaliseGradingSolana,
    finaliseGradingWithBatching,
    getMetaMaskProvider,
} from '../../blockchainInteraction/finaliseGrading';
import WhiteCard from '../../ui/WhiteCard';
import TextWithTooltip from '../../ui/TextWithTooltip';
import { Connection } from '@solana/web3.js';
import { getTransferTransaction, phantomConnection } from '../../blockchainInteraction/phantomConnection';
import { useWallet } from '@solana/wallet-adapter-react';
import { getSPLTokenBalance, getTokenBalance } from '../../apis/geenpills/mintNFT';
import { RPC_ENDPOINT_L } from '../../constants/apiPaths';
import { BrowserProvider, formatEther } from 'ethers';

// Component for ranks 4 and above
const HigherRank = ({ rank }) => (
    <svg width="24" height="23" viewBox="0 0 24 23" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 19.2618L17.8198 22.7914C18.8856 23.4383 20.1898 22.4821 19.9094 21.2727L18.3667 14.6353L23.5134 10.1635C24.453 9.3479 23.9482 7.80105 22.7141 7.70261L15.9407 7.12606L13.2902 0.854283C12.8134 -0.284761 11.1866 -0.284761 10.7098 0.854283L8.05935 7.112L1.28591 7.68855C0.0518278 7.78699 -0.453025 9.33384 0.486562 10.1494L5.63325 14.6213L4.09065 21.2586C3.81017 22.468 5.11437 23.4242 6.18017 22.7774L12 19.2618Z"
            fill="#E08F66"
        />
        <text
            x="12"
            y="14"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="white"
            fontSize="10"
            fontWeight="bold"
            fontFamily="Arial, sans-serif"
        >
            {rank}
        </text>
    </svg>
);

const getRankIcon = (rankNumber) => {
    if (rankNumber === 1) return <FirstRank />;
    if (rankNumber === 2) return <SecondRank />;
    if (rankNumber === 3) return <ThirdRank />;
    return <HigherRank rank={rankNumber} />;
};

// Function to get smart rank for a submission based on score and distribution type
const getSmartRank = (submissionId, rewardDistribution) => {
    if (!rewardDistribution || rewardDistribution.length === 0) return 1;

    // Find current submission
    const currentSubmission = rewardDistribution.find(s => s.submissionId === submissionId);
    if (!currentSubmission) return 1;

    // Check if this is equal reward distribution by looking at reward amounts
    const rewardAmounts = rewardDistribution.map(r => r.rewardAmount);
    const isEqualDistribution = rewardAmounts.every(amount => amount === rewardAmounts[0]);

    if (isEqualDistribution) {
        // For equal distribution, check if all rewarded submissions have the same score
        const validSubmissions = rewardDistribution.filter(s => s.accuracy >= 0);
        const allScoresEqual = validSubmissions.every(s => s.accuracy === validSubmissions[0].accuracy);

        if (allScoresEqual) {
            // All tied - everyone gets first place
            return 1;
        }
    }

    // For rank-based distribution or unequal scores, use score-based ranking
    const submissionsSorted = rewardDistribution
        .filter(s => s.accuracy >= 0)
        .sort((a, b) => b.accuracy - a.accuracy);

    // Find the rank based on score (handle ties)
    let rankPosition = 1;
    let currentScore = submissionsSorted[0]?.accuracy;

    for (let i = 0; i < submissionsSorted.length; i++) {
        if (submissionsSorted[i].accuracy < currentScore) {
            rankPosition = i + 1;
            currentScore = submissionsSorted[i].accuracy;
        }

        if (submissionsSorted[i].submissionId === submissionId) {
            return rankPosition;
        }
    }

    return 1;
};
function RequestValidation() {
    const { bountyId } = useParams();

    const { loading, startLoading, stopLoading } = useLoadingState();
    const { id } = useParams();
    const { loading: processing, startLoading: startProcessing, stopLoading: stopProcessing } = useLoadingState();
    const [account, setAccount] = useState();
    const [preview, setPreview] = useState({});
    const navigate = useNavigate();
    const wallet = useWallet();
    const { sdk, provider } = useSDK();

    // useEffect(async () => {
    //     try {
    //         const accounts = await sdk?.connect();
    //         console.log(accounts);
    //         setAccount(accounts?.[0]);
    //     } catch (err) {
    //         console.warn('failed to connect..', err);
    //     }
    // }, [sdk]);
    useEffect(
        function () {
            const fetchData = async () => {
                try {
                    startLoading();

                    const data = await getValidationPreview(bountyId);

                    setPreview(data);
                    stopLoading();
                } catch (error) {
                    console.log(error);
                }
            };
            fetchData();
        },
        [bountyId]
    );
    const totalRewardAmount = preview?.rewardDistribution?.reduce((acc, item) => acc + item?.rewardAmount, 0);
    // const onSubmittingRequest = async () => {
    //     try {
    //         startProcessing();
    //         const accounts = await sdk?.connect();
    //         // if chain id is not same , we have to prompt the user to change the network right now just displaying a toast message
    //         console.log(preview?.tokenInfo?.chainId, chainId, accounts);
    //         if (preview?.tokenInfo?.chainId != chainId) {
    //             toast.error('Please change your chain network');
    //             return;
    //         }

    //         const payload = {
    //             tokenAddress: preview?.tokenInfo.address,
    //             rewardsDistribution: preview?.rewardDistribution,
    //             provider: provider,
    //             decimals: preview?.tokenInfo.decimals,
    //             sdk: sdk,
    //             wallet: preview?.wallet,
    //             platformFee: preview?.platformFee,
    //             bountyId: bountyId,
    //             chainId: preview?.tokenInfo?.chainId,
    //         };
    //         // wallet , platformFee

    //         const balance = await getERC20Balance(payload?.tokenAddress, payload?.wallet, payload?.chainId);

    //         if (balance > totalRewardAmount + payload?.platformFee) {
    //             toast.error('Not enough token in your wallet');
    //             return;
    //         }
    //         console.log(balance, 'see the balance');
    //         const response2 = await finaliseGrading(payload);
    //         const response = await validating(bountyId);

    //         console.log(accounts, response);
    //         setAccount(accounts?.[0]);
    //     } catch (err) {
    //         console.warn('failed to connect..', err);
    //     } finally {
    //         stopProcessing();
    //     }
    // };

    const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);

    const onSubmittingRequest = async (closeModal) => {

        console.log("now request the payout")
        try {
            startProcessing();

            if (preview?.fiatCurrency?.id) {
                const { success } = await requestINRFundTransfer(bountyId)
                if (true) {
                    navigate(`/organizations/${id}/validation`, { replace: true });
                }
                return
            }

            if (preview?.tokenInfo?.chainName === 'solana') {
                const phantom = wallet.wallets.find((w) => w.adapter.name === 'Phantom');

                const phantomConnectionResponse = await phantomConnection(wallet);

                if (phantomConnectionResponse === 'chainerror') {
                    return;
                } else {
                    const tokenBalance = await getSPLTokenBalance(
                        phantomConnectionResponse,
                        preview?.tokenInfo?.address
                    );
                    const totalRewardAmount = preview?.rewardDistribution?.reduce(
                        (acc, ele) => acc + Number(ele?.rewardAmount),
                        0
                    );

                    if (tokenBalance < Number(totalRewardAmount) + Number(preview?.platformFee)) {
                        // For Solana, we might want to show the same modal or keep existing behavior?
                        // The request specifically mentioned Metamask wallet and chains like Arbitrum, Base etc.
                        // So I will keep Solana behavior as is for now unless requested otherwise, 
                        // but the prompt implies this feature is for the EVM flow.
                        // However, "Change Wallet or Reward Token" could apply to Solana too.
                        // Given the specific chains mentioned (Arbitrum, Base, etc.), I'll focus on EVM.
                        toast.error(
                            `Insufficient ${preview?.tokenInfo?.name}  : You need  ${Number(totalRewardAmount)} ${preview?.tokenInfo?.symbol
                            }`
                        );
                        return;
                    }

                    const connection = new Connection(RPC_ENDPOINT_L, 'confirmed');
                    console.log(preview, 'before solana processing');
                    const { success, code } = await finaliseGradingSolana(
                        preview?.tokenInfo?.address,
                        preview?.rewardDistribution,
                        phantomConnectionResponse,
                        Number(toNormalRepresentation(preview?.platformFee, preview?.tokenInfo.decimals)),
                        preview?.tokenInfo.decimals,
                        bountyId,
                        preview?.tokenInfo?.chainId,
                        connection,
                        phantom,
                        preview?.wallet
                    );

                    if (!success) {
                        // toast.error(`May be winner has not linked metamask wallet in his "Impact Miner" account`);
                        toast.error('Transaction failed');
                        return;
                    }
                    if (success) {
                        const response = await validating(bountyId);

                        setAccount(phantomConnectionResponse);

                        navigate(`/organizations/${id}/validation`);
                    }
                }
            } else {
                // Get MetaMask provider
                const metaMaskProvider = getMetaMaskProvider();
                if (!metaMaskProvider) {
                    const installUrl = 'https://metamask.io/download.html';
                    toast.info('Opening MetaMask installation page in a new tab...');
                    window.open(installUrl, '_blank'); // Open the URL in a new tab
                    // toast.error('MetaMask is not installed or unavailable');
                    return;
                }

                // Request accounts using MetaMask provider
                const accounts = await metaMaskProvider.request({ method: 'eth_requestAccounts' });
                if (!accounts || accounts.length === 0) {
                    toast.error('Failed to connect wallet');
                    return;
                }

                // Detect chain ID
                const userChainId = await metaMaskProvider.request({ method: 'eth_chainId' });
                const targetChainId = `0x${parseInt(preview?.tokenInfo?.chainId, 10).toString(16)}`; // Convert to hex string

                if (userChainId !== targetChainId) {
                    try {
                        // Attempt to switch to the correct chain
                        await metaMaskProvider.request({
                            method: 'wallet_switchEthereumChain',
                            params: [{ chainId: targetChainId }],
                        });
                        toast.success(`Switched to chain ID: ${targetChainId}`);
                    } catch (switchError) {
                        // Handle the case where the chain is not added or user rejects the switch

                        if (switchError.code === 4902) {
                            // 4902: Chain not added to MetaMask
                            try {
                                const chainDetails = await fetchChainDetails(preview?.tokenInfo?.chainId);
                                await metaMaskProvider.request({
                                    method: 'wallet_addEthereumChain',
                                    params: [chainDetails],
                                });
                                toast.success(`Added and switched to chain ID: ${targetChainId}`);
                            } catch (addError) {
                                toast.error('Failed to add or switch to the required chain');
                                return;
                            }
                        } else {
                            toast.error('Please change your chain network manually');
                            return;
                        }
                    }
                }

                const requiredTokenAmount = preview?.rewardDistribution?.reduce(
                    (acc, ele) => acc + Number(ele?.rewardAmount),
                    0
                );

                const tokenBalance = await getTokenBalance(
                    preview?.tokenInfo?.chainId,
                    preview?.tokenInfo?.address,
                    accounts[0]
                );

                const tokenBalanceComparable = Number(Number(tokenBalance) / 10 ** preview?.tokenInfo?.decimals);
                const providerMeta = new BrowserProvider(metaMaskProvider);
                console?.log('see balance');
                const signer = await providerMeta.getSigner();
                const address = await signer.getAddress();
                const balance = await providerMeta.getBalance(address);
                const balanceEth = parseFloat(formatEther(balance));

                if (balanceEth === 0) {
                    toast.error(
                        `Insufficient ${preview?.tokenInfo?.chainId === 42220 ? 'CELO' : 'ETH'} : You have 0 ${preview?.tokenInfo?.chainId === 42220 ? ' CELO' : ' ETH'
                        }`
                    );

                    return;
                }

                // Check if balance is sufficient
                if (tokenBalanceComparable < requiredTokenAmount + Number(preview?.platformFee)) {
                    // Show Insufficient Balance Modal
                    setShowInsufficientBalanceModal(true);
                    if (closeModal) closeModal(); // Close the TokenApprovalModal trigger if it was open (though here we are inside the trigger logic)
                    return;
                }

                // If we are here, balance is sufficient. 
                // But wait, the original code opened TokenApprovalModal via the UI <Modal> component.
                // The `onSubmittingRequest` was passed TO `TokenApprovalModal`.
                // So `onSubmittingRequest` is actually the function that executes the TRANSFER.
                // The user wants the check BEFORE opening `TokenApprovalModal`?
                // "if not have enough token then on clicking this button ... should open a modal to show chain and tokens ... and if have enough token then keep the existing flow that is open TokenApprovalModal"

                // The existing flow is: Button -> Modal (TokenApprovalModal) -> User clicks "Okay" -> onSubmittingRequest.
                // So `onSubmittingRequest` is the ACTUAL transfer logic.
                // The user wants to intercept the INITIAL click.

                // Currently:
                // <Modal>
                //    <Modal.Button opens={'tokenapproval'}>Process Payout</Modal.Button>
                //    <Modal.Window name={'tokenapproval'}><TokenApprovalModal ... /></Modal.Window>
                // </Modal>

                // I need to change this structure.
                // I should make the button a regular button that calls a function `handleProcessPayoutClick`.
                // This function checks balance.
                // If sufficient -> Open TokenApprovalModal (manually or via state).
                // If insufficient -> Open InsufficientBalanceModal.

                // So I need to refactor the JSX part first.
                // And `onSubmittingRequest` should remain as the "Transfer" logic called BY `TokenApprovalModal`.

                // Let's revert this change to `onSubmittingRequest` and instead create a new handler `handleProcessPayoutClick`.

                // Prepare payload
                console.log(
                    Number(toNormalRepresentation(preview?.platformFee)),
                    preview,
                    'before payload setting',
                    preview?.tokenInfo.decimals
                );
                const payload = {
                    tokenAddress: preview?.tokenInfo?.address,
                    rewardsDistribution: preview?.rewardDistribution,
                    provider: provider,
                    decimals: preview?.tokenInfo.decimals,
                    sdk: sdk,
                    wallet: preview?.wallet,
                    platformFee: Number(toNormalRepresentation(preview?.platformFee, preview?.tokenInfo.decimals)),
                    bountyId: bountyId,
                    chainId: preview?.tokenInfo?.chainId,
                };

                const { success, code } = await finaliseGradingWithBatching(payload);

                if (!success) {
                    // toast.error(`May be winner has not linked metamask wallet in his "Impact Miner" account`);
                    toast.error(code);
                    return;
                }
                if (success) {
                    const response = await validating(bountyId);

                    setAccount(accounts?.[0]);

                    navigate(`/organizations/${id}/validation`);
                }
            }
        } catch (err) {
            console.warn('Failed to connect..', err);
            toast.error('An error occurred while processing your request');
        } finally {
            stopProcessing();
            console.log(closeModal)
            if (closeModal) {
                closeModal();
            }
        }
    };
    console.log(preview, preview?.rewardDistribution?.length, 'now implement solana rewarding');
    const [showTokenApprovalModal, setShowTokenApprovalModal] = useState(false);
    const [checkingBalance, setCheckingBalance] = useState(false);

    const [connectedAccount, setConnectedAccount] = useState(null);

    const handleProcessPayoutClick = async () => {

        if (preview?.fiatCurrency?.id) {
            onSubmittingRequest(false);
            return;
        }

        if (preview?.tokenInfo?.chainName === 'solana') {
            // setShowTokenApprovalModal(true);
            // return;
            onSubmittingRequest(false);
            return;
        }

        // EVM Balance Check
        try {
            setCheckingBalance(true);
            const metaMaskProvider = getMetaMaskProvider();
            if (!metaMaskProvider) {
                const installUrl = 'https://metamask.io/download.html';
                window.open(installUrl, '_blank');
                return;
            }

            const accounts = await metaMaskProvider.request({ method: 'eth_requestAccounts' });
            if (!accounts || accounts.length === 0) {
                toast.error('Failed to connect wallet');
                return;
            }
            setConnectedAccount(accounts[0]);

            // Switch chain if needed
            const userChainId = await metaMaskProvider.request({ method: 'eth_chainId' });
            const targetChainId = `0x${parseInt(preview?.tokenInfo?.chainId, 10).toString(16)}`;

            if (userChainId !== targetChainId) {
                try {
                    await metaMaskProvider.request({
                        method: 'wallet_switchEthereumChain',
                        params: [{ chainId: targetChainId }],
                    });
                } catch (switchError) {
                    if (switchError.code === 4902) {
                        try {
                            const chainDetails = await fetchChainDetails(preview?.tokenInfo?.chainId);
                            await metaMaskProvider.request({
                                method: 'wallet_addEthereumChain',
                                params: [chainDetails],
                            });
                        } catch (addError) {
                            toast.error('Failed to add or switch to the required chain');
                            return;
                        }
                    } else {
                        toast.error('Please switch chain manually');
                        return;
                    }
                }
            }

            const requiredTokenAmount = preview?.rewardDistribution?.reduce(
                (acc, ele) => acc + Number(ele?.rewardAmount),
                0
            );

            const tokenBalance = await getTokenBalance(
                preview?.tokenInfo?.chainId,
                preview?.tokenInfo?.address,
                accounts[0]
            );

            const tokenBalanceComparable = Number(Number(tokenBalance) / 10 ** preview?.tokenInfo?.decimals);
            console.log(tokenBalanceComparable < requiredTokenAmount + Number(preview?.platformFee), "is this coming")
            if (tokenBalanceComparable < requiredTokenAmount + Number(preview?.platformFee)) {
                setShowInsufficientBalanceModal(true);
            } else {
                setShowTokenApprovalModal(true);
            }
        } catch (error) {
            console.error("Error checking balance", error);
            toast.error("Failed to check balance");
        } finally {
            setCheckingBalance(false);
        }
    };
    console.log(preview, "see this preview if has dollar")
    return (
        <div>
            {loading ? (
                <div className="h-40 col-span-2 bg-grayBgNormal flex mt-6 items-center justify-center">
                    <Loader />
                </div>
            ) : (
                <>
                    <Table minHeight="" className=" items-center gap-4 max-height-[45vh] " cols="grid-cols-12">
                        <Table.Header>
                            <p className="text-sm   font-semibold text-secondaryText "> Rank </p>
                            <p className="text-sm col-span-2  font-semibold text-secondaryText "> Submission ID </p>
                            <p className="text-sm justify-self-center  font-semibold text-secondaryText ">Score</p>
                            <p className="text-sm justify-self-center col-span-2 font-semibold text-secondaryText ">
                                Reward Token
                            </p>
                            <p className="text-sm justify-self-center col-span-2  font-semibold text-secondaryText ">
                                Impact Points
                            </p>
                            <p className="text-sm col-span-3  font-semibold text-secondaryText ">Impact Certificate</p>
                        </Table.Header>
                        {preview?.rewardDistribution?.length > 0 ? (
                            preview?.rewardDistribution?.map((submission, index) => (
                                <Table.Row key={submission.submissionId}>
                                    <p className="     uppercase justify-self-start  ">
                                        <span>{getRankIcon(getSmartRank(submission.submissionId, preview?.rewardDistribution))}</span>
                                    </p>
                                    <p
                                        onClick={() =>
                                            navigate(
                                                `/organizations/${id}/validation/bounty-submissions/${bountyId}/${submission.submissionId}`
                                            )
                                        }
                                        className=" col-span-2 text-textBrandBlue flex items-center cursor-pointer  uppercase justify-self-start font-medium  "
                                    >
                                        <span className="truncate w-32">{submission.submissionId} </span>{' '}
                                        {/* <span>{index + 1 <= 3 && rank[index + 1]}</span> */}
                                    </p>
                                    <p className="justify-self-center  ">
                                        {submission.accuracy
                                            ? toNormalRepresentation(Number(submission.accuracy) * 100)
                                            : '-'}
                                    </p>
                                    <p className="justify-self-center col-span-2  ">
                                        {' '}
                                        <span> {preview?.fiatCurrency?.symbol ? preview?.fiatCurrency?.symbol : ""} {toNormalRepresentation(submission.rewardAmount)} </span>{' '}
                                        <span>{preview?.rewardTokenSymbol}</span>{' '}
                                    </p>
                                    <p className="justify-self-center col-span-2  ">
                                        {submission.impactPoints ? Number(submission.impactPoints).toFixed(2) : '-'}
                                    </p>
                                    <p className="col-span-3 ">
                                        {submission?.issueImpactCertificate ? <IssuedIcon /> : <NotIssuedIcon />}
                                    </p>
                                </Table.Row>
                            ))
                        ) : (
                            <NoDataAvailable text="No person will be awarded upon validation" />
                        )}
                    </Table>
                    {preview?.rewardDistribution?.length > 0 && (
                        <WhiteCard className="px-6 py-5">
                            <div className="mb-3">
                                <div className="flex items-center mb-2 justify-between">
                                    <p className="text-textPrimary text-xl">Amount disbursed</p>
                                    <div className="flex items-center gap-1">
                                        {
                                            preview?.fiatCurrency?.id ? <p className="text-textPrimary font-medium text-xl">{preview?.fiatCurrency?.symbol}</p> : <div className="relative">
                                                <img
                                                    className="w-5 h-5 rounded-full "
                                                    src={preview?.tokenInfo?.image}
                                                    alt=""
                                                />
                                                <img
                                                    className="w-2 h-2 absolute top-0 right-0 rounded-full "
                                                    src={preview?.tokenInfo?.chainImage}
                                                    alt=""
                                                />
                                            </div>
                                        }

                                        <p className="text-textPrimary font-medium text-xl">{totalRewardAmount}</p>
                                    </div>
                                </div>
                                <div className="flex items-center  mb-2 justify-between">
                                    {!preview?.fiatCurrency?.id && <TextWithTooltip
                                        heading={<p className="text-textPrimary text-xl">Platform Fee</p>}
                                    >
                                        <p className="text-sm">
                                            We charge a 0.5% platform fee on the total disbursed amount
                                        </p>
                                    </TextWithTooltip>}

                                    <div className="flex items-center gap-1">

                                        {
                                            !preview?.fiatCurrency?.id && <div className="relative">
                                                <img
                                                    className="w-5 h-5 rounded-full "
                                                    src={preview?.tokenInfo?.image}
                                                    alt=""
                                                />
                                                <img
                                                    className="w-2 h-2 absolute top-0 right-0 rounded-full "
                                                    src={preview?.tokenInfo?.chainImage}
                                                    alt=""
                                                />
                                            </div>
                                        }
                                        {!preview?.fiatCurrency?.id && <p className="text-textPrimary font-medium text-xl">{preview?.platformFee}</p>}

                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center pt-3 border-t border-borderUnderline   justify-between">
                                <p className="text-textPrimary text-2xl font-semibold">Total</p>
                                <div className="flex items-center gap-1">
                                    {
                                        preview?.fiatCurrency?.id ? <p className="text-textPrimary font-medium text-xl">{preview?.fiatCurrency?.symbol}</p> : <div className="relative">
                                            <img className="w-5 h-5 rounded-full " src={preview?.tokenInfo?.image} alt="" />
                                            <img
                                                className="w-2 h-2 absolute top-0 right-0 rounded-full "
                                                src={preview?.tokenInfo?.chainImage}
                                                alt=""
                                            />
                                        </div>
                                    }


                                    <p className="text-textPrimary font-bold text-xl">
                                        {totalRewardAmount + preview?.platformFee}
                                    </p>
                                </div>
                            </div>
                        </WhiteCard>
                    )}
                    <div className="flex justify-end items-center">
                        <div className="30% ">
                            <ButtonNew
                                onClick={handleProcessPayoutClick}
                                variant="blue"
                                size="md"
                                shape="rounded"
                                disabled={processing || checkingBalance}
                            >
                                {processing || checkingBalance ? <Loader /> : 'Process Payout'}
                            </ButtonNew>

                            {showTokenApprovalModal && (
                                <TokenApprovalModal
                                    onSubmittingRequest={onSubmittingRequest}
                                    processing={processing}
                                    numberOfWinners={preview?.rewardDistribution?.length}
                                    close={() => setShowTokenApprovalModal(false)}
                                />
                            )}

                            {showInsufficientBalanceModal && (
                                <InsufficientBalanceModal
                                    close={() => setShowInsufficientBalanceModal(false)}
                                    bountyId={bountyId}
                                    currentToken={preview?.tokenInfo}
                                    account={connectedAccount || account}
                                    provider={provider}
                                    totalPayRequired={preview?.
                                        totalPayoutAmountUSD}

                                />
                            )}
                        </div>
                    </div>
                    <div className="flex items-center justify-end mt-6">
                        <div className="flex items-center gap-3 ">
                            <p className="text-sm font-bold">Impact Certificate</p>
                            <Legend shield={<NotIssuedIcon />} text={'Not Issued'} />
                            <Legend shield={<IssuedIcon />} text={'Issued'} />
                        </div>
                    </div>
                    <div className="flex justify-end items-center">
                        {/* <Modal>
                            <Modal.Button
                                className="flex gap-2 cursor-pointer items-center"
                                opens={'validationrequest'}
                            >
                                <Button size="xsmall">Submit Request</Button>
                            </Modal.Button>
                            <Modal.Window name={'validationrequest'}>
                                <ValidationCreationModal
                                    infoRequired={{ lastUpdateTime: bountyDetails?.maxUpdateTime }}
                                />
                            </Modal.Window>
                        </Modal> */}

                        {/* <div className="30%">
                            {false ? (
                                <Button onClick={() => {}} size="small">
                                    {false ? <Loader /> : 'Submit Request'}
                                </Button>
                            ) : (
                                <div className="w-full flex items-center">
                                    <ConnectMetamaskWallet />
                                </div>
                            )}
                        </div> */}
                    </div>
                </>
            )}
        </div>
    );
}

export default RequestValidation;
// {
// 	"organisationId": "6606d2c0ed51ae4d31a51ae3",
// 	"requestType": "BOUNTY_VALIDATION", // OR "BOUNTY_CREATION
// 	"bountyId": "6606d2c0ed51ae4d31a51gdsg",
// 	// need to send the max value of `lastUpdateTime` from the response of GET /organisation/validation/submission/<submissionId>
// 	"lastUpdateTime": "2024-03-29T14:40:00.179Z"
// }
